import './ShortlistMetadataEdit.css'

import get from 'lodash/get'
import omit from 'lodash/omit'
import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import { Link, NavigateFunction } from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { getMergedAlias, getUserAlias } from '@disco/utils/aliases'

import { AliasEntity } from 'types/playlist'
import type { Shortlist } from 'types/shortlist'
import type {
  EditableTrack,
  Track,
  TrackAlias,
  TrackAliasValue,
  TrackId,
} from 'types/track'
import type { User } from 'types/user'

import { getShortlistBaseRoute } from 'utils/routing'

import MetadataFields from 'components/common/tracks/metadata-fields'
import AliasTrackOrderField from 'components/common/tracks/metadata-fields/alias-track-order-field'
import { Tab, Tabs } from 'components/ui'
import Alert from 'components/ui/alert'
import Checkbox from 'components/ui/checkbox'

import IndividualTab from './individual-tab'
import MetadataNote from './metadata-note'
import { Box, DiscoCallout } from 'components/ds'

const getTrackOrderFieldValue = (
  playlistAliases,
): 'Reset' | 'PlaylistOrder' | 'ExistingOrder' => {
  const trackNumberInclude = get(playlistAliases, 'TrackNumber.include', true)
  // order is important here
  if (!trackNumberInclude) return 'Reset'

  if (get(playlistAliases, '[X-Disco-Auto-TrackNumber].include', null)) {
    return 'PlaylistOrder'
  }

  return 'ExistingOrder'
}

type Props = {
  params: {
    shortlistId: string
  }
  shortlist: Shortlist
  editedTrack: EditableTrack & {
    loadingMetadata?: boolean
    loadingMetadataError?: boolean
    trackIds?: TrackId[]
  }
  originalEditedTrackMetadata: Record<string, any>
  hideMoreButton: boolean
  navigate: NavigateFunction
  user: User
  updateShortlist: (..._args: Array<any>) => any
  initShortlistEditedTrack: (..._args: Array<any>) => any
  updateEditedTrack: (..._args: Array<any>) => any
  updateArtwork: (..._args: Array<any>) => any
  sendMixpanelEvent: typeof sendMixpanelEvent
  isTrial: boolean
  individualEditingEnabled: boolean
  batchEditingEnabled: boolean
  tabIndex: number
  location: any
  bottomRowRef: {
    current: HTMLElement | null | undefined
  }
  tabOpen: boolean
}

class ShortlistMetadataEdit extends Component<
  Props,
  {
    tabIndex: number
    playlistAliases: AliasEntity['playlist_aliases']
    trackAliases: AliasEntity['track_aliases']
  }
> {
  constructor(props: Props) {
    super(props)
    this.state = {
      tabIndex: 0,
      playlistAliases: props.shortlist.playlist_aliases || {
        'X-Disco-Auto-TrackNumber': {
          include: true,
          value: '',
        },
      },
      trackAliases: props.shortlist.track_aliases || {},
    }
  }

  componentDidMount() {
    this.props.initShortlistEditedTrack(this.props.shortlist.id)
  }

  _handleTabChange: (_tabIndex: number) => void = (tabIndex: number) =>
    this.setState({
      tabIndex,
    })

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!nextProps.shortlist) {
      this.props.navigate('/')
    }

    if (this.props.tabOpen && !nextProps.tabOpen) {
      this.saveAliases()
    }

    const playlistAliases = this.props.shortlist?.playlist_aliases || {}
    const nextPlaylistAliases = nextProps.shortlist?.playlist_aliases || {}
    const trackAliases = this.props.shortlist?.track_aliases || {}
    const nextTrackAliases = nextProps.shortlist?.track_aliases || {}
    const aliasesMetadataRemoved =
      (Object.keys(playlistAliases).length > 0 &&
        Object.keys(nextPlaylistAliases).length === 0) ||
      (Object.keys(trackAliases).length > 0 &&
        Object.keys(nextTrackAliases).length === 0)

    if (aliasesMetadataRemoved) {
      this.setState({
        playlistAliases: nextProps.shortlist.playlist_aliases || {
          'X-Disco-Auto-TrackNumber': {
            include: true,
            value: '',
          },
        },
        trackAliases: nextProps.shortlist.track_aliases || {},
      })
    }
  }

  render(): null | React.ReactElement<'div'> {
    const {
      shortlist,
      editedTrack,
      originalEditedTrackMetadata,
      user,
      isTrial,
      individualEditingEnabled,
      bottomRowRef,
    } = this.props
    if (!shortlist) return null
    const tracks = shortlist.items.map(item => item.track)
    const trackOrderFieldValue = getTrackOrderFieldValue(
      this.state.playlistAliases,
    )
    const baseRoute = getShortlistBaseRoute(this.props.location)

    const individualEditingTab = (
      <IndividualTab
        parentAlias={getMergedAlias(
          this.props.shortlist,
          user,
          this.state.playlistAliases,
        )}
        aliases={this.state.trackAliases}
        tracks={tracks}
        onAliasChange={this._onAliasChange}
        isTrial={isTrial}
        individualEditingEnabled={individualEditingEnabled}
      />
    )
    return (
      <div className="EditMetadata EditMetadata--extended">
        <Box mb="4">
          <DiscoCallout type="warning-important">
            Alias metadata does not apply to nested tracks
          </DiscoCallout>
        </Box>
        {bottomRowRef?.current && individualEditingEnabled
          ? ReactDOM.createPortal(
              <Link
                className="Button--removeAll"
                to={`${baseRoute}/shortlists/${shortlist.id}/metadata/reset`}
              >
                Remove all alias metadata for this playlist
              </Link>,
              bottomRowRef?.current,
            )
          : null}
        {this.props.batchEditingEnabled ? (
          <Tabs
            className="Tabs--blue-back"
            index={this.state.tabIndex}
            onChange={this._handleTabChange}
          >
            <Tab label="Batch edit">
              <MetadataNote />
              {isTrial && (
                <React.Fragment>
                  <Alert>
                    Batch edit metadata alias functionality is available
                    on&nbsp;
                    <Link to="/settings/billing">Plus and Pro plans.</Link>
                  </Alert>
                  <br />
                </React.Fragment>
              )}
              <MetadataFields
                track={shortlist.items[0].track}
                originalEditedTrackMetadata={originalEditedTrackMetadata}
                editedTrack={editedTrack}
                beforeLabelRender={this._renderCheckbox}
                showDifference
                showExtraMetadata
                disableTrackOrder
                disableTrackTitle
                hideMoreButton={this.props.hideMoreButton}
                updateEditedTrack={this._playlistUpdateHandler}
                fieldsHelpText={{
                  Title: (
                    <div className="EditTrackMetadata_HelpText">
                      Want to edit the Title? Click{' '}
                      <a onClick={() => this._handleTabChange(1)}>
                        here to edit individually
                      </a>
                    </div>
                  ),
                  Artwork: (
                    <div className="EditTrackMetadata_HelpText">
                      Drag artwork here to add artwork just for this playlist
                    </div>
                  ),
                }}
                fieldsCustomRenderers={{
                  TrackNumber: (
                    <AliasTrackOrderField
                      value={trackOrderFieldValue}
                      onChange={this._handleAliasTrackOrderChange}
                    />
                  ),
                }}
                role="alias"
              />
            </Tab>
            <Tab label="Edit individually">{individualEditingTab}</Tab>
          </Tabs>
        ) : (
          individualEditingTab
        )}
      </div>
    )
  }

  _handleAliasTrackOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    switch (e.target.value) {
      case 'Reset':
        this._toggleIncludingAliasByName('TrackNumber', false)

        break

      case 'PlaylistOrder':
        this._toggleIncludingAliasByName('X-Disco-Auto-TrackNumber', true)

        break

      case 'ExistingOrder':
        this._toggleIncludingAliasByName('TrackNumber', true)

        break
    }
  }
  // gets alias include state based on combination of current user alias,
  // user alias of saved playlist(if exists) and current playlist alias
  _getAliasIncludeState: (_aliasName: string) => any | boolean = (
    aliasName: string,
  ) => {
    const mergedAlias = getMergedAlias(
      this.props.shortlist,
      this.props.user,
      this.state.playlistAliases,
    )
    const checked = mergedAlias
      ? (!mergedAlias[aliasName] && aliasName !== 'X-Disco-StripUnknown') ||
        get(mergedAlias, `${aliasName}.include`, null)
      : true
    return checked
  }
  _renderCheckbox: (
    _aliasName: string,
  ) => React.ReactElement<'div'> | React.ReactNode = (aliasName: string) => {
    // placeholder instead of actual checkbox
    if (aliasName === 'Title') return <div className="Checkbox">&nbsp;</div>

    const checked = this._getAliasIncludeState(aliasName)

    const handler =
      aliasName === 'TrackNumber' &&
      get(this.state.playlistAliases, 'TrackNumber.include', null) === false
        ? () => this._toggleIncludingAliasByName('X-Disco-Auto-TrackNumber')
        : () => this._toggleIncludingAliasByName(aliasName)
    return <Checkbox checked={!!checked} onChange={handler} />
  }
  _playlistUpdateHandler: (
    _track: Record<string, any>,
    _imageFile: File | null | undefined,
  ) => void = (track, imageFile) => {
    const { metadata } = track
    const { editedTrack } = this.props
    let newAliases = {}
    let omitKeys = []
    Object.keys(metadata).forEach(key => {
      if (metadata[key]?.value !== editedTrack.metadata[key]?.value) {
        // if value matches original value, set value to empty string as
        // user don't actually modifies anything
        newAliases[key] = {
          value:
            metadata[key]?.value ===
            this.props.originalEditedTrackMetadata[key]?.value
              ? ''
              : metadata[key]?.value,
          include: true,
        }
        omitKeys.push(key)
      }
    })
    let playlistAliases = { ...this.state.playlistAliases, ...newAliases }

    if (
      playlistAliases['Artwork']?.value === '' &&
      playlistAliases['Artwork']?.include
    ) {
      playlistAliases = omit(playlistAliases, 'Artwork')
    }

    this.setState({
      playlistAliases,
      trackAliases: this._omitTrackAliasKeys(omitKeys),
    })
    this.props.updateArtwork(imageFile)
    this.props.updateEditedTrack(track)
  }
  _omitTrackAliasKeys: (_keys: Array<string>) => Record<number, TrackAlias> = (
    keys: string[],
  ) => {
    const updatedTrackAliases = {}
    Object.keys(this.state.trackAliases).forEach(key => {
      updatedTrackAliases[key] = omit(
        this.state.trackAliases[parseInt(key, 10)],
        keys,
      )
    })
    return updatedTrackAliases
  }
  _onAliasChange: (
    _trackId: number,
    _key: string,
    _value: string | null | undefined,
  ) => void = (
    trackId: number,
    key: string,
    value: string | null | undefined,
  ) => {
    const mergedAlias = getMergedAlias(
      this.props.shortlist,
      this.props.user,
      this.state.playlistAliases,
    )
    const mergedAliasValue = mergedAlias[key] ? mergedAlias[key].value : null
    const mergedAliasInclude = mergedAlias[key]
      ? mergedAlias[key].include
      : null
    const valueCanBeOmitted = // included playlist field can be '', this just means that original
      // metadata must be included, if value set to '' we actually need
      // to strip metadata, and this value can't be omitted
      (value === mergedAliasValue && mergedAliasInclude && value !== '') || // if playlist field is not included, if value is '', it can be omitted
      // cause it's parent value already strips metadata from the tracks,
      // explicit check required, if mergedAliasInclude is null we shouldn't
      // omit track alias value
      (mergedAliasInclude === false && value === '') || // if value is null we just need to omit it
      value === null
    const trackUpdate = !valueCanBeOmitted
      ? {
          ...this.state.trackAliases[trackId],
          [key]: {
            include: value !== '',
            value,
          },
        }
      : omit(this.state.trackAliases[trackId], key)
    this.setState({
      trackAliases: { ...this.state.trackAliases, [trackId]: trackUpdate },
    })
  }
  _toggleIncludingAliasByName: (
    _aliasName: string,
    _overrideValue?: boolean | null | undefined,
  ) => void = (
    aliasName: string,
    overrideValue: boolean | null | undefined = null,
  ) => {
    const userAlias = getUserAlias(this.props.shortlist, this.props.user)

    // if alias for this prop doesn't exist, this means that value should be
    // included
    const prevValue = this._getAliasIncludeState(aliasName)

    const newValue = get(
      this.props.editedTrack.metadata,
      `${aliasName}.value`,
      '',
    )
    const { playlistAliases } = this.state
    let newAliases = {
      ...playlistAliases,
      [aliasName]: {
        value: newValue,
        include: overrideValue !== null ? overrideValue : !prevValue,
      },
    }
    const defaultValue =
      aliasName !== 'X-Disco-StripUnknown' &&
      aliasName !== 'X-Disco-Auto-TrackNumber'
    const userAliasIncludeValue = get(
      userAlias,
      `${aliasName}.include`,
      defaultValue,
    )

    if (
      userAliasIncludeValue === newAliases[aliasName].include &&
      (aliasName === 'X-Disco-StripUnknown' ||
        newValue ===
          get(this.props.originalEditedTrackMetadata, `${aliasName}.value`, ''))
    ) {
      // value can be omitted
      newAliases = omit(newAliases, aliasName)
    }

    // setting TrackNumber removes X-Disco-Auto-TrackNumber
    if (aliasName === 'TrackNumber') {
      newAliases = {
        ...newAliases,
        'X-Disco-Auto-TrackNumber': {
          include: false,
          value: '',
        },
      }
    }

    if (aliasName === 'X-Disco-Auto-TrackNumber') {
      newAliases = {
        ...newAliases,
        TrackNumber: {
          include: true,
          value: '',
        },
      }
    }

    this.setState({
      playlistAliases: newAliases,
      // new value will be false and we must omit current track alias value
      trackAliases: prevValue
        ? this._omitTrackAliasKeys([aliasName])
        : this.state.trackAliases,
    })
  }
  saveAliases: () => void = () => {
    this.props.updateShortlist(this.props.shortlist.id, {
      playlist_aliases: this.state.playlistAliases,
      track_aliases: this.state.trackAliases,
    })

    if (Object.keys(this.state.trackAliases).length > 0) {
      this.props.sendMixpanelEvent('Individual edit – overwrite metadata')
    }

    if (
      Object.keys(this.state.playlistAliases).some(
        aliasName =>
          !get(
            this.state.playlistAliases,
            `[${aliasName}].include`,
            aliasName !== 'X-Disco-StripUnknown',
          ),
      )
    ) {
      this.props.sendMixpanelEvent('Batch edit – switch fields off')
    }

    if (
      Object.keys(this.state.playlistAliases).some(aliasName =>
        get(this.state.playlistAliases, `[${aliasName}].value`, null),
      )
    ) {
      this.props.sendMixpanelEvent('Batch edit – overwrite metadata')
    }
  }
}

export default ShortlistMetadataEdit
